import { useContext, useEffect, useMemo, useState, useCallback } from "react";
import "./MyChatUnanswered.scss";
import { AuthContext } from "../../../contexts/AuthContext";
import PageTitle from "../../../components/PageTitle/PageTitle";
import AlertModal from "../../../components/AlertModal/AlertModal";
import { ReactComponent as User } from "../../../assets/user.svg";
import { ReactComponent as Check } from "../../../assets/check-circle.svg";
import DisplayInfo from "../../../components/DisplayInfo/DisplayInfo";
import Pagination from "../../../components/Pagination/Pagination";

export default function MyUnansweredMessages() {
  const { axiosAuth, accessibleGroups = [] } = useContext(AuthContext);

  // grupo único do cliente (ou primeiro acessível)
  const groupId = useMemo(
    () => (accessibleGroups.length ? String(accessibleGroups[0].id) : ""),
    [accessibleGroups]
  );

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  const openError = useCallback((title, message) => {
    setErrorModal({ open: true, title, message });
  }, []);
  const closeError = useCallback(
    () => setErrorModal({ open: false, title: "", message: "" }),
    []
  );

  // paginação
  const [page, setPage] = useState(1);
  const limit = 8;
  const [total, setTotal] = useState(0);
  const totalPages = useMemo(() => Math.ceil(total / limit), [total]);

  // estado para saber quais mensagens estão a ser marcadas
  const [updatingIds, setUpdatingIds] = useState(() => new Set());

  // util: formata data/hora dd-mm-aaaa hh:mm
  const formatDateTime = (dateInput) => {
    if (!dateInput) return "-";
    const d = new Date(dateInput);
    if (Number.isNaN(d.getTime())) return "-";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const nn = String(d.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${yyyy} ${hh}:${nn}`;
  };

  // carrega mensagens do grupo e filtra por is_fallback
  useEffect(() => {
    setMessages([]);
    if (!groupId) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const { data } = await axiosAuth.get(
          `/chat-messages/group/${groupId}/unaswered`,
          {
            params: {
              skip: (page - 1) * limit,
              limit,
            },
          }
        );
        const results = Array.isArray(data) ? data : data?.results || [];
        const totalCount = Number.isFinite(data?.total) ? data.total : 0;
        setMessages(results);
        setTotal(totalCount);
      } catch (err) {
        if (!cancelled) {
          openError(
            "Erro ao carregar mensagens",
            err?.response?.data?.detail || err.message
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [groupId, axiosAuth, openError, page, limit]);

  const handleMarkAsChecked = useCallback(
    async (messageId) => {
      // marcar como atualizar (desativa o botão)
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.add(messageId);
        return next;
      });

      try {
        await axiosAuth.patch(`/chat-messages/${messageId}/checked`);

        // remover da UI
        setMessages((prev) => prev.filter((m) => m.id !== messageId));

        // atualizar total para manter paginação coerente
        setTotal((prev) => Math.max(0, prev - 1));
      } catch (err) {
        openError(
          "Erro ao marcar mensagem como revista.",
          err?.response?.data?.detail || err.message
        );
      } finally {
        // limpar estado de loading dessa mensagem
        setUpdatingIds((prev) => {
          const next = new Set(prev);
          next.delete(messageId);
          return next;
        });
      }
    },
    [axiosAuth, openError]
  );

  return (
    <div className="my-unanswered-messages">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || "Ocorreu um erro"}
          message={errorModal.message}
          onClose={closeError}
        />
      )}

      <PageTitle title="Mensagens sem resposta" />

      <div className="container">
        <div className="messages-list">
          {!loading && !groupId && (
            <DisplayInfo
              variant="danger"
              message="Não tem grupos acessíveis."
            />
          )}
          {!loading && !groupId && messages.length === 0 && (
            <DisplayInfo
              variant="success"
              message="Sem mensagens não respondidas."
            />
          )}

          {messages.map((m) => (
            <div key={`${m.session_id}-${m.id}`} className="content">
              <button
                type="button"
                onClick={() => handleMarkAsChecked(m.id)}
                disabled={loading || updatingIds.has(m.id)}
                className="checked-btn"
              >
                <Check />
              </button>
              <div className="top">
                <div className="left">
                  <div className="icon">
                    <User />
                  </div>
                  <span className="green"></span>
                </div>
                <div className="right">
                  <span>Sessão: {m.session_id}</span>
                  <p className="date">{formatDateTime(m.timestamp)}</p>
                </div>
              </div>
              <div className="bottom">
                <p className="message">{m.visitor_question || ""}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {total > limit && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(e) => setPage(e)}
        />
      )}
    </div>
  );
}
